import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";

type AnalyticsConfig = {
  googleAnalyticsId?: string | null;
};

declare global {
  interface Window {
    dataLayer?: any[];
    gtag?: (...args: any[]) => void;
  }
}

function normalizeGaId(value: unknown): string {
  const id = String(value || "").trim();
  return /^G-[A-Z0-9]+$/i.test(id) ? id.toUpperCase() : "";
}

/**
 * Queues the GA configuration without loading anything.
 *
 * `gtag.js` is the single heaviest third-party script on the site: the mobile run
 * measured it at 172.1 KiB with 95.5 KiB unused, and it was being fetched
 * alongside the app bundle — competing for bandwidth with the hero image that
 * decides LCP, and executing on the same main thread React is booting on.
 *
 * The standard `gtag()` snippet only ever *queues* onto `dataLayer`; the real
 * library drains that queue when it eventually loads. So the stub can be
 * installed immediately (page views keep working, in order) while the 172 KiB
 * download is pushed past first paint. See `scheduleGtagScript`.
 */
function installGtagQueue(id: string) {
  window.dataLayer = window.dataLayer || [];
  window.gtag =
    window.gtag ||
    function gtag() {
      // Google interprets Arguments as gtag commands; arrays use a different data-layer format.
      window.dataLayer?.push(arguments);
    };

  const configured = document.documentElement.dataset.gaConfigured || "";
  if (configured !== id) {
    window.gtag("js", new Date());
    window.gtag("config", id, { send_page_view: false });
    document.documentElement.dataset.gaConfigured = id;
  }
}

/**
 * Loads the tag only once the critical path is genuinely finished.
 *
 * The previous version called `requestIdleCallback(inject, { timeout: 3500 })`
 * straight away. Chrome's first idle gap lands *during* hydration, so the
 * ~172 KiB download started while React was still booting and while the hero
 * image that decides LCP was still in flight. The mobile run still scored gtag
 * at 172.5 KiB of transfer with 95.9 KiB of it unused, an 84 ms long task, and
 * a place in the network-dependency tree — the whole point of deferring it was
 * to keep it out of that window, and it was not out of it.
 *
 * The automatic path now waits for `load` first. `load` is by definition after
 * the first paint and after the LCP element's request has completed, so the tag
 * can no longer compete for the Slow 4G link or the main thread React is using.
 *
 * Queued page views are processed when the library arrives, provided the visitor
 * is still on the page. Visitors leaving before the loader runs may go uncounted.
 *
 * A deliberate user interaction still injects immediately — that is a better
 * signal of "settled" than any timer, and the interaction must not be measured
 * late. The absolute backstop guarantees analytics is never lost entirely if
 * `load` never fires because one request stalls.
 */
const GTAG_IDLE_TIMEOUT_MS = 4000;
const GTAG_BACKSTOP_MS = 15000;

function scheduleGtagScript(id: string) {
  if (document.getElementById(`ga-loader-${id}`)) return;

  const events: (keyof WindowEventMap)[] = ["pointerdown", "keydown", "scroll", "touchstart"];
  let injected = false;
  let backstop = 0;

  const cleanup = () => {
    events.forEach((event) => window.removeEventListener(event, inject));
    window.clearTimeout(backstop);
  };

  const inject = () => {
    if (injected) return;
    injected = true;
    cleanup();

    const loader = document.createElement("script");
    loader.id = `ga-loader-${id}`;
    loader.async = true;
    loader.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
    document.head.appendChild(loader);
  };

  events.forEach((event) => window.addEventListener(event, inject, { once: true, passive: true }));

  // Safety net: a stalled request must not silently disable analytics.
  backstop = window.setTimeout(inject, GTAG_BACKSTOP_MS);

  const afterLoad = () => {
    const idle = (window as any).requestIdleCallback as
      | ((cb: () => void, opts?: { timeout: number }) => number)
      | undefined;
    if (typeof idle === "function") idle(inject, { timeout: GTAG_IDLE_TIMEOUT_MS });
    else window.setTimeout(inject, 0);
  };

  if (document.readyState === "complete") afterLoad();
  else window.addEventListener("load", afterLoad, { once: true });
}

export function Analytics() {
  const [location] = useLocation();
  const lastTrackedRef = useRef("");

  const { data } = useQuery<AnalyticsConfig>({
    queryKey: ["/api/seo"],
    queryFn: async () => {
      const res = await fetch("/api/seo");
      if (!res.ok) return {};
      return res.json();
    },
    staleTime: 5 * 60 * 1000,
  });

  const gaId = normalizeGaId(data?.googleAnalyticsId);

  useEffect(() => {
    if (!gaId) return;
    installGtagQueue(gaId);
    scheduleGtagScript(gaId);
  }, [gaId]);

  useEffect(() => {
    if (!gaId || typeof window === "undefined" || typeof document === "undefined") return;
    if (!window.gtag) return;

    const href = window.location.href;
    const key = `${gaId}:${href}`;
    if (lastTrackedRef.current === key) return;
    lastTrackedRef.current = key;

    window.gtag("event", "page_view", {
      page_title: document.title,
      page_location: href,
      page_path: location,
    });
  }, [gaId, location]);

  return null;
}
